import axios from 'axios';
import { reportNetworkFailure, reportNetworkSuccess } from './connectivity';

// CRA bakes REACT_APP_API_URL into the bundle at BUILD time. A build run
// without it used to fall back to localhost, which only ever works on the
// machine that also runs the backend - the deployed site then loads fine on
// the developer's PC and fails on every phone. Treat a localhost API on a
// deployed page as a broken build rather than shipping it silently.
const PRODUCTION_API_URL = 'https://drsoftapi.erp-softgroup.link/api';
const LOCAL_API_URL = 'http://localhost:5055/api';

const isLocalPage = typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1', ''].includes(window.location.hostname);

const resolveApiUrl = () => {
    const configured = process.env.REACT_APP_API_URL;

    // Running against a dev server: honour whatever is configured, localhost included.
    if (isLocalPage) {
        return configured || LOCAL_API_URL;
    }

    if (!configured || /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/i.test(configured)) {
        console.error(
            `[api] This build has REACT_APP_API_URL="${configured || ''}", which is not reachable ` +
            `from a deployed page. Falling back to ${PRODUCTION_API_URL}. Rebuild with ` +
            `REACT_APP_API_URL=${PRODUCTION_API_URL} to fix this properly.`
        );
        return PRODUCTION_API_URL;
    }

    return configured;
};

export const API_URL = resolveApiUrl();

// ---- retries ---------------------------------------------------------------
//
// The clinics run on connections that drop for seconds at a time. A request
// that never reached the server (no response at all - not a 4xx or 5xx) is
// retried a few times with a growing pause before the page is told about it.
//
// Reads are always safe to retry. Writes are retried only once the server has
// told us it honours the Idempotency-Key header (every reply from a server
// that does carries X-Idempotency-Support), because retrying a write blindly
// against an older API could save the same visit twice. The key is generated
// once per request and reused on every retry, which is what makes the retry
// safe: the server recognises the repeat and answers from memory.

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);
const MAX_READ_RETRIES = 3;
const MAX_WRITE_RETRIES = 2;
const BASE_DELAY_MS = 500;

let idempotencySupported = false;
export const serverSupportsIdempotency = () => idempotencySupported;

const randomHex = (bytes) => {
    let out = '';
    for (let i = 0; i < bytes; i += 1) {
        out += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
    }
    return out;
};

export const newIdempotencyKey = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now().toString(16)}-${randomHex(12)}`;
};

// A failure with no reply at all: DNS, refused, dropped mid-flight, timed out.
// A 4xx/5xx is the server talking to us, and is never retried here.
export const isNetworkError = (error) =>
    Boolean(error) && !error.response && error.code !== 'ERR_CANCELED' && error.name !== 'CanceledError';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const browserSaysOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

const retryBudget = (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (!MUTATING.has(method)) return MAX_READ_RETRIES;
    if (!idempotencySupported) return 0;
    if (!config.headers || !config.headers['Idempotency-Key']) return 0;
    return MAX_WRITE_RETRIES;
};

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // One key per write, minted on the first attempt and carried through
        // every retry of it. A caller may set its own (a queued offline write
        // will, so a replay days later still maps to the same key).
        const method = (config.method || 'get').toLowerCase();
        if (MUTATING.has(method) && !config.headers['Idempotency-Key']) {
            config.headers['Idempotency-Key'] = newIdempotencyKey();
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle response errors
api.interceptors.response.use(
    (response) => {
        reportNetworkSuccess();
        if (response.headers && response.headers['x-idempotency-support']) {
            idempotencySupported = true;
        }
        return response;
    },
    async (error) => {
        // Any reply, even an error one, means the server is reachable.
        if (error.response) {
            reportNetworkSuccess();
            if (error.response.headers && error.response.headers['x-idempotency-support']) {
                idempotencySupported = true;
            }
        }

        if (isNetworkError(error) && error.config && !error.config.noRetry) {
            const config = error.config;
            const attempt = config.__retryCount || 0;

            // With the interface itself down there is nothing to wait for: fail
            // now so the page shows the offline state immediately.
            if (attempt < retryBudget(config) && !browserSaysOffline()) {
                config.__retryCount = attempt + 1;
                await sleep(BASE_DELAY_MS * (2 ** attempt));
                return api.request(config);
            }

            reportNetworkFailure();
        }

        // A 401 from the login call itself just means bad credentials - let the
        // login form report it instead of reloading the page out from under it.
        const isLoginRequest = (error.config?.url || '').includes('/auth/login');
        const alreadyOnLogin = window.location.pathname === '/login';

        if (error.response?.status === 401 && !isLoginRequest) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (!alreadyOnLogin) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export default api;
